import React, {
  useState,
  useCallback,
  useRef,
  useContext,
  useEffect,
  useLayoutEffect,
} from "react";
import { getTimelinePosition } from "../utils/timeline";
import { getTodayPositionForYearRange, isCurrentYearInRange } from "../utils/date";
import { groupMilestonesByDate } from "../utils/milestone";
import { TaskBar } from "./TaskBar";
import { ResourceMoniteringContext } from "../../resourcePlanner/resourceMonitoring/ResourceContext";
import { useDispatch } from "react-redux";
import { DependencyModal } from "./DependencyModal";
import { DependencyLines } from "./DependencyLine";
import { ProgressBar } from "./ProgressBar";
import { BaselineTask } from "./BaselineTask";
import { TimelineGrid } from "./TimelineViews/Grid";
import { MilestoneMarker } from "./MilestoneMarker";
import moment from "moment/moment";

export default function Timeline({
  reference,
  scopes,
  expandedScopes,
  viewType,
  showTaskModal,
  setTaskStartDate,
  taskDates,
  setTaskDates,
  handleTaskModal,
  componentHeight,
  critical,
  displayYearRange,
  overviewType,
  isHeaderCollapsed = false,
  isHideTask = false,
  hoveredRowId,
  setHoveredRowId
}) {


  const [currentDate] = useState(new Date());
  const [dragging, setDragging] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const {
    project,
    setPeerTasks,
    baselineScopes,
    baselinez,
    markTaskAsCompleted,
    duplicateTask,
    updateTaskDates,
    addTaskDependencies,
    milestones,
    settings
  } = useContext(ResourceMoniteringContext);
  const dragStartRef = useRef({ x: 0, startDate: null, endDate: null });
  const dispatch = useDispatch();
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [targetScope, setTargetScope] = useState(null);
  const [task, setTask] = useState({});
  const [activePopup, setActivePopup] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    elementId: null,
  });

  const displayMinDate = moment().year(displayYearRange?.start).startOf("year");
  const displayMaxDate = moment().year(displayYearRange?.end).endOf("year");

  const [timelineScrollHeight, setTimelineScrollHeight] = useState(0);

  useLayoutEffect(() => {
    const el = reference.current;
    if (!el) return;
    setTimelineScrollHeight(el.scrollHeight);
  }, [scopes, expandedScopes, baselineScopes, reference]);

  useEffect(() => {
    const handleScroll = (event) => {
      if (activePopup.isVisible) {
        setActivePopup(() => ({
          isVisible: false,
          position: { x: 0, y: 0 },
          elementId: null,
        }));
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [activePopup]);

  const calculateDaysDiff = useCallback(
    (pixelDiff, viewType) => {
      const CELL_WIDTH = 241;
      const CELL_WIDTH_QUARTER = 501;
      switch (viewType.toLowerCase()) {
        case "monthly": {
          const daysInCurrentMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          ).getDate();
          const dayWidth = CELL_WIDTH / daysInCurrentMonth;
          return Math.round(pixelDiff / dayWidth);
        }
        case "weekly": {
          const dayWidth = CELL_WIDTH / 7;
          return Math.round(pixelDiff / dayWidth);
        }
        case "daily": {
          return Math.round(pixelDiff / CELL_WIDTH);
        }
        case "quarter": {
          const month = currentDate.getMonth();
          const quarterStartMonth = Math.floor(month / 3) * 3;
  
          let daysInQuarter = 0;
          for (let i = 0; i < 3; i++) {
            const m = quarterStartMonth + i;
            daysInQuarter += new Date(currentDate.getFullYear(), m + 1, 0).getDate();
          }
  
          const dayWidth = CELL_WIDTH_QUARTER / daysInQuarter;
          return Math.round(pixelDiff / dayWidth);
        }
        default:
          return 0;
      }
    },
    [currentDate]
  );

  const handleDragStart = useCallback(
    (e, taskId, type, name) => {
      if (!reference.current) return;

      const timelineRect = reference.current.getBoundingClientRect();
      const task = taskDates[taskId];

      dragStartRef.current = {
        x: e.clientX - timelineRect.left,
        startDate: new Date(task.startDate),
        endDate: new Date(task.endDate),
        taskLeft: getTimelinePosition(
          task.startDate,
          task.endDate,
          viewType,
          currentDate,
          displayYearRange
        ).left,
      };
      
      setDragging({ taskId, type, name });
      setIsDragging(true);
      
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = type === 'move' ? 'grabbing' : 'ew-resize';
    },
    [reference, taskDates, viewType, currentDate]
  );

  const handleDragMove = useCallback(
    (e) => {
      if (!dragging || !reference.current) return;
      
      const timelineRect = reference.current.getBoundingClientRect();
      const currentX = e.clientX - timelineRect.left;
      const pixelDiff = currentX - dragStartRef.current.x;
      const daysDiff = calculateDaysDiff(pixelDiff, viewType);

      setTaskDates((prev) => {
        const newDates = { ...prev[dragging.taskId] };
        const originalStartDate = dragStartRef.current.startDate;
        const originalEndDate = dragStartRef.current.endDate;
        const taskDuration = originalEndDate - originalStartDate;

        if (dragging.type === "move" && originalStartDate) {
          const newStart = new Date(originalStartDate);
          newStart.setDate(newStart.getDate() + daysDiff);
          const newEnd = new Date(originalStartDate.getTime() + taskDuration);
          newEnd.setDate(newEnd.getDate() + daysDiff);

          if (
            newStart >= displayMinDate &&
            newEnd <= displayMaxDate &&
            newStart < newEnd
          ) {
            const newEnd = new Date(newStart.getTime() + taskDuration);
            return {
              ...prev,
              [dragging.taskId]: {
                startDate: newStart.toISOString().split("T")[0],
                endDate: newEnd.toISOString().split("T")[0],
              },
            };
          }
        } else if (dragging.type === "start") {
          const newStart = new Date(originalStartDate);
          newStart.setDate(newStart.getDate() + daysDiff);

          if (
            newStart >= displayMinDate &&
            newStart < originalEndDate
          ) {
            return {
              ...prev,
              [dragging.taskId]: {
                ...newDates,
                startDate: newStart.toISOString().split("T")[0],
              },
            };
          }
        } else if (dragging.type === "end") {
          const newEnd = new Date(originalEndDate);
          newEnd.setDate(newEnd.getDate() + daysDiff);

          if (newEnd > originalStartDate && newEnd <= displayMaxDate) {
            return {
              ...prev,
              [dragging.taskId]: {
                ...newDates,
                endDate: newEnd.toISOString().split("T")[0],
              },
            };
          }
        }

        return prev;
      });
    },
    [dragging, viewType, calculateDaysDiff]
  );

  const handleDragEnd = useCallback(() => {
    if (!dragging) return;
    
    // Restore normal cursor and selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    
    const wasActuallyDragging = isDragging;
    
    requestAnimationFrame(() => {
      setDragging(null);
      setIsDragging(false);
      dragStartRef.current = { x: 0, startDate: null, endDate: null };
    });
    
    if (!wasActuallyDragging) return;
    
    const updatedTask = taskDates[dragging.taskId];
    if (!updatedTask) return;
    
    const payload = {
      startDate: updatedTask.startDate,
      endDate: updatedTask.endDate,
    };
    updateTaskDates(dragging.taskId, payload);
  }, [dragging, isDragging, taskDates, updateTaskDates]);

  const getTimelineLength = useCallback(() => {
    let length = scopes?.length * 50 + 45;
    if (baselineScopes?.length) {
      length += baselineScopes.length * 30;
    }

    for (const scope of scopes) {
      if (expandedScopes[scope.id]) {
        length += scope.tasks.length * 20;
        length += 20;
      }
    }
    for (const baselineScope of baselineScopes) {
      if (expandedScopes[baselineScope.id]) {
        length += baselineScope.tasks.length * 20;
      }
    }
    return length;
  }, [scopes, baselineScopes, expandedScopes]);

  const handleDayClick = useCallback(
    (e, dayId, startDate) => {
      e.preventDefault();
      const date = new Date(startDate).toISOString().split("T")[0];
      setTaskStartDate(() => date);

      if (activePopup.isVisible && activePopup.elementId === dayId) {
        setActivePopup({
          isVisible: false,
          position: { x: 0, y: 0 },
          elementId: null,
        });
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      setActivePopup({
        isVisible: true,
        position: {
          x: e.clientX,
          y: e.clientY,
        },
        elementId: dayId,
      });
    },
    [activePopup.isVisible, activePopup.elementId]
  );

  const handleCreateTask = (e, scopeIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const targetScope = scopes[scopeIndex];
    setActivePopup({
      isVisible: false,
      position: { x: 0, y: 0 },
      elementId: null,
    });
    showTaskModal(targetScope, true);
  };

  const handleDependencySave = (task, dependencies) => {
    setPeerTasks(() => []);
    setTask(() => {});
    setShowDependencyModal(() => false);
    if (!dependencies) return;
    const newDependencies = dependencies.map((item) => ({
      id: item.task.id,
      dependencyType: item.dependencyType,
    }));
    addTaskDependencies(task.id, newDependencies);
  };

  const handleAddDependency = (scopeIndex, task) => {
    const peerTasks = scopes?.[scopeIndex].tasks.filter(
      (item) => item.id !== task.id
    );
    setPeerTasks(() => peerTasks);
    setTask((prev) => ({
      ...prev,
      ...task,
      scopeId: scopes?.[scopeIndex]?.id ?? null,
    }));
    setShowDependencyModal(() => true);
  };

  const clickHandler = (e, index) => {
    const createTaskElement = e.target.closest(".handle-create-task");
    const dayClickElement = e.target.closest(".handle-day-click");

    if (createTaskElement) {
      handleCreateTask(e, index);
    } else if (dayClickElement) {
      const dayId = dayClickElement.getAttribute("data-day-id");
      const day = dayClickElement.getAttribute("data-day");
      handleDayClick(e, dayId, day);
    }
  };

  const groupedMilestones = groupMilestonesByDate(milestones, viewType, displayYearRange);

  return (
    <div
      onMouseMove={handleDragMove}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      ref={reference}
      className="relative overflow-y-auto overflow-x-hidden timeline-container"
      style={{
        height: `${viewType === "daily"
            ? `calc(100vh - ${overviewType === "Project" ? (isHeaderCollapsed ? 305 : 506) : 246}px)`
            : `calc(100vh - ${overviewType === "Project" ? (isHeaderCollapsed ? 327 : 531) : 271}px)`
          }`,
      }}
      onClick={(e) => clickHandler(e, targetScope)}
    >
      {viewType !== "daily" && isCurrentYearInRange(displayYearRange?.start, displayYearRange?.end) && (
        <div
          className="absolute top-0 w-[2px] bg-[#342C82] z-1 "
          style={{
            left: `${getTodayPositionForYearRange(viewType, displayYearRange?.start, displayYearRange?.end) - 12}px`,
            height: `${timelineScrollHeight}px`,
          }}
        >
          <div className="relative -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#342C82] rotate-45" />
        </div>
      )}

      {viewType !== "daily" && groupedMilestones.map((group, index) => (
        <MilestoneMarker
          key={`milestone-group-${index}`}
          milestones={group.milestones}
          position={group.position}
          timelineHeight={getTimelineLength()}
          settings={settings}
        />
      ))}

      {scopes.map((scope, index) => (
        <div 
          key={scope.id} 
          className={`border-b ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
        >
          <div
            onMouseEnter={() => setHoveredRowId(scope.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            className={`${baselineScopes?.length > 0 ? "h-[100px]" : "h-[52px]"} relative  ${hoveredRowId === scope.id && "!bg-[#ece7fd]"}`}
            onClick={(e) => { setTargetScope(() => index);}}
          >
            <ProgressBar
              scope={scope}
              getTimelinePosition={getTimelinePosition}
              viewType={viewType}
              displayYearRange={displayYearRange}
            />
            <ProgressBar
              baseline={baselinez?.[0]}
              scope={baselineScopes[index]}
              getTimelinePosition={getTimelinePosition}
              viewType={viewType}
              isBaseline={true}
              displayYearRange={displayYearRange}
            />
          </div>
          {expandedScopes[scope.id] && (
            <div className="flex flex-col gap-2">
              {scope.tasks.map((task, i) => {
                const dates = taskDates[task.id];
                const { left, width, shouldRender } = getTimelinePosition(
                  dates?.startDate,
                  dates?.endDate,
                  viewType,
                  currentDate,
                  displayYearRange
                );
                let baselineScopePositioning;

                if (baselineScopes && baselineScopes.length > 0) {
                  if (
                    baselineScopes[index] &&
                    baselineScopes[index]?.tasks &&
                    baselineScopes[index]?.tasks.length > 0
                  ) {
                    const { startDate, endDate } =
                      baselineScopes[index]?.tasks?.[i] ?? {};

                    if (startDate && endDate) {
                      baselineScopePositioning = getTimelinePosition(
                        startDate,
                        endDate,
                        viewType,
                        currentDate,
                        displayYearRange
                      );
                    }
                  }
                }

                return (
                  <React.Fragment 
                    key={`task-fragment-${task.id}`}>
                    <div
                      onMouseEnter={() => setHoveredRowId(task.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`h-[20px] relative  ${hoveredRowId === task.id && "!bg-[#ece7fd]"}`}
                      onClick={(e) => {
                        setTargetScope(() => index);
                      }}
                      
                    >
                      {shouldRender && (
                        <TaskBar
                          critical={critical}
                          handleDragStart={handleDragStart}
                          handleAddDependency={() => {
                            handleAddDependency(index, task);
                          }}
                          handleTaskModal={() => {
                            setActivePopup({
                              isVisible: false,
                              position: { x: 0, y: 0 },
                              elementId: null,
                            });
                            setTimeout(() => handleTaskModal(task), 200);
                          }}
                          handleMarkCompleted={() => {
                            markTaskAsCompleted(task.id);
                            setActivePopup({
                              isVisible: false,
                              position: { x: 0, y: 0 },
                              elementId: null,
                            });
                          }}
                          handleDuplicateTask={() => {
                            duplicateTask(task.id);
                            setActivePopup({
                              isVisible: false,
                              position: { x: 0, y: 0 },
                              elementId: null,
                            });
                          }}
                          task={task}
                          left={left + 5}
                          width={width}
                          setActivePopup={setActivePopup}
                          isHideTask={isHideTask}
                        />
                      )}
                    </div>
                    {baselineScopes && baselineScopes.length > 0 && (
                      <div
                        key={`baseline-${task.id}-${index}`}
                        className={`h-[20px] relative `}
                        onClick={(e) => {
                          setTargetScope(() => index);
                        }}
                      >
                        {baselineScopePositioning?.shouldRender && (
                          <BaselineTask
                            left={baselineScopePositioning.left}
                            width={baselineScopePositioning.width}
                            task={baselineScopes?.[index]?.tasks?.[i]}
                            baseline={baselinez?.[0]}
                            isHideTask={isHideTask}
                          />
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              <div
                className={`h-[20px] relative`}
                onClick={(e) => {
                  setTargetScope(() => index);
                }}
              >
              </div>
            </div>
          )}
        </div>
      ))}

      <DependencyLines
        tasks={scopes.flatMap((scope) => scope.tasks)}
        taskDates={taskDates}
        viewType={viewType}
        getHeight={getTimelineLength}
      />

      {showDependencyModal && (
        <DependencyModal
          task={task}
          onSave={(dependencies) => {
            handleDependencySave(task, dependencies);
          }}
          onClose={() => {
            setPeerTasks(() => []);
            setTask(() => {});
            setShowDependencyModal(() => false);
          }}
        />
      )}
    </div>
  );
}